import { vpSnap, withDebugViewport } from "@/lib/viewportDiagnostics";

const GOOGLE_OAUTH_PENDING_KEY = "tl-google-oauth-pending";
const GOOGLE_OAUTH_RELOAD_KEY = "tl-google-oauth-clean-load";
const GOOGLE_OAUTH_DECISION_KEY = "tl-google-oauth-decision";
export const GOOGLE_OAUTH_MAX_AGE_MS = 10 * 60 * 1000;

export type PendingGoogleOAuth = {
  /** One-time id proving this tab started the flow. */
  nonce: string;
  /** Internal destination to land on after the clean load. */
  target: string;
  startedAt: number;
  /** Diagnostics panel was active when the flow started. */
  debug: boolean;
};

/** Explicit, persisted record of why recovery ran or was skipped. */
export type OAuthRecoveryDecision = {
  at: string;
  path: string;
  pendingMarkerExists: boolean;
  expired: boolean;
  hasSession: boolean;
  alreadyRecovered: boolean;
  eligible: boolean;
  reason: string;
  target: string | null;
};

/** Only internal, non-protocol-relative paths are ever navigated to. */
export function safeTarget(value: string | null | undefined): string {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : "/app/dashboard";
}

function readJson<T>(key: string): T | null {
  try {
    const value = sessionStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
}

function createNonce(): string {
  const bytes = new Uint32Array(4);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(36)).join("");
}

function recordDecision(decision: OAuthRecoveryDecision): void {
  try {
    sessionStorage.setItem(GOOGLE_OAUTH_DECISION_KEY, JSON.stringify(decision));
  } catch {
    /* diagnostics must never break auth */
  }
  vpSnap("oauth-recovery-decision", decision as unknown as Record<string, unknown>);
}

export function readOAuthRecoveryDecision(): OAuthRecoveryDecision | null {
  return readJson<OAuthRecoveryDecision>(GOOGLE_OAUTH_DECISION_KEY);
}

export function readPendingGoogleOAuth(): PendingGoogleOAuth | null {
  return readJson<PendingGoogleOAuth>(GOOGLE_OAUTH_PENDING_KEY);
}

/**
 * Stores everything needed for the return *in sessionStorage* — the hosted
 * OAuth broker does not preserve our callback query string — and returns the
 * same-origin redirect URI to hand to the provider.
 */
export function beginGoogleOAuth(target: string): string {
  sessionStorage.removeItem(GOOGLE_OAUTH_RELOAD_KEY);
  const debug = new URLSearchParams(window.location.search).get("debugViewport") === "1";
  const pending: PendingGoogleOAuth = {
    nonce: createNonce(),
    target: withDebugViewport(safeTarget(target)),
    startedAt: Date.now(),
    debug,
  };
  sessionStorage.setItem(GOOGLE_OAUTH_PENDING_KEY, JSON.stringify(pending));
  vpSnap("oauth-begin", { target: pending.target });

  const callback = new URL("/auth", window.location.origin);
  return callback.toString();
}

export function clearPendingGoogleOAuth(): void {
  sessionStorage.removeItem(GOOGLE_OAUTH_PENDING_KEY);
}

let recoveredInThisDocument = false;

/**
 * Pure state machine for the recovery decision — exported for tests.
 * Recovery requires a fresh pending marker *and* an authenticated session.
 * It never depends on the current pathname or on any query parameter.
 */
export function evaluateGoogleOAuthReturn(input: {
  pending: PendingGoogleOAuth | null;
  hasSession: boolean;
  now: number;
  alreadyRecovered: boolean;
}): { eligible: boolean; reason: string; target: string | null } {
  const { pending, hasSession, now, alreadyRecovered } = input;
  if (alreadyRecovered) return { eligible: false, reason: "already-recovered-in-document", target: null };
  if (!pending) return { eligible: false, reason: "no-pending-marker", target: null };
  if (!pending.nonce) return { eligible: false, reason: "malformed-pending-marker", target: null };
  if (now - pending.startedAt > GOOGLE_OAUTH_MAX_AGE_MS)
    return { eligible: false, reason: "pending-marker-expired", target: null };
  if (!hasSession) return { eligible: false, reason: "no-session-yet", target: null };
  return { eligible: true, reason: "eligible-google-oauth-return", target: safeTarget(pending.target) };
}

/**
 * Called once the app knows whether a session exists, from any route.
 * Performs a single clean full-document navigation and returns true when the
 * caller should stop rendering because a navigation is under way.
 */
export function maybeRecoverGoogleOAuthReturn(hasSession: boolean): boolean {
  if (typeof window === "undefined") return false;
  const pending = readPendingGoogleOAuth();
  const now = Date.now();
  const result = evaluateGoogleOAuthReturn({
    pending,
    hasSession,
    now,
    alreadyRecovered: recoveredInThisDocument,
  });

  const base = {
    at: new Date(now).toISOString(),
    path: `${window.location.pathname}${window.location.search}`,
    pendingMarkerExists: !!pending,
    expired: !!pending && now - pending.startedAt > GOOGLE_OAUTH_MAX_AGE_MS,
    hasSession,
    alreadyRecovered: recoveredInThisDocument,
  };

  if (!result.eligible) {
    // An expired / malformed marker must not linger and re-arm later.
    if (pending && (base.expired || !pending.nonce)) clearPendingGoogleOAuth();
    // Only record noteworthy decisions to keep the log readable.
    if (pending) recordDecision({ ...base, ...result });
    return false;
  }

  recordDecision({ ...base, ...result });
  recoveredInThisDocument = true;
  hardNavigateAfterGoogleOAuth(result.target!);
  return true;
}

export function hardNavigateAfterGoogleOAuth(target: string): void {
  const finalTarget = withDebugViewport(safeTarget(target));
  vpSnap("oauth-hard-navigate", { target: finalTarget });
  // Consume the pending marker BEFORE navigating: the next document must not
  // be able to recover again.
  clearPendingGoogleOAuth();
  sessionStorage.setItem(
    GOOGLE_OAUTH_RELOAD_KEY,
    JSON.stringify({ target: finalTarget, completedAt: Date.now() }),
  );
  window.location.replace(finalTarget);
}

/** Consumes the one-time clean-load marker on the fresh document. */
export function initOAuthReturnDiagnostics(): void {
  const cleanLoad = readJson<{ target: string; completedAt: number }>(GOOGLE_OAUTH_RELOAD_KEY);
  if (!cleanLoad) return;
  sessionStorage.removeItem(GOOGLE_OAUTH_RELOAD_KEY);
  if (Date.now() - cleanLoad.completedAt > GOOGLE_OAUTH_MAX_AGE_MS) return;
  vpSnap("oauth-clean-load", { target: cleanLoad.target });
}
